import { RoleGate, useAuth, UserRole } from "@/features/auth";
import { ReleaseHighlightMenu } from "@/features/release-highlight";
import {
    Badge,
    Button,
    Container,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Icon,
    Kbd,
    KbdGroup,
    Logo,
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/shared/components";
import { useColorScheme } from "@/shared/hooks";
import { useNotificationPermissions } from "@/shared/hooks/notifications/use-notifications.ts";
import { useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router";

function PreviewRolesMenu() {
    const { previewRole, setPreviewRole, hasRole } = useAuth();

    if (!hasRole(UserRole.ADMIN, true)) {
        return null;
    }

    const handleToggleRole = (role: UserRole) => {
        setPreviewRole(previewRole === role ? null : role);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild data-testid="preview-roles">
                <Button icon="eye" variant="tertiary" iconOnly />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="text-content-default w-80" align="end" data-testid="preview-roles">
                <DropdownMenuLabel>Role Preview</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                    {Object.values(UserRole).map((role, index) => (
                        <DropdownMenuItem key={role} onClick={() => handleToggleRole(role)} data-selected={previewRole === role}>
                            <Icon name={previewRole === role ? "check" : "user"} size="xs" className="mr-2" />
                            <span className="flex-1 capitalize">{role}</span>
                            <KbdGroup>
                                <Kbd>Ctrl</Kbd>
                                <Kbd>{index + 1}</Kbd>
                            </KbdGroup>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function CurrentRoleBadge() {
    const { previewRole, hasRole } = useAuth();
    const adRoles = Object.values(UserRole).filter((role) => hasRole(role, true));

    if (!hasRole(UserRole.ADMIN, true)) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {adRoles.map((role) => (
                <Badge key={role} variant="gray-subtle" size="m" className="capitalize">
                    {role}
                </Badge>
            ))}
            {previewRole && (
                <Badge variant="yellow" size="m" className="capitalize">
                    Preview: {previewRole}
                </Badge>
            )}
        </div>
    );
}

export function ThemeToggleDropdown() {
    const [theme, setTheme] = useColorScheme();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button icon="dark-light-mode" variant="tertiary" iconOnly title="Donkere modus" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="text-content-default w-64" align="end">
                <DropdownMenuItem
                    className="data-[selected=true]:bg-selected-default data-[selected=true]:text-content-selected justify-between"
                    onClick={() => setTheme("light")}
                    data-selected={theme === "light"}
                >
                    <span className="flex items-start">
                        <Icon name="sun" size="m" className="mr-2" />
                        Licht
                    </span>
                    {theme === "light" && <Icon name="check" size="m" className="" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="data-[selected=true]:bg-selected-default data-[selected=true]:text-content-selected justify-between"
                    onClick={() => setTheme("dark")}
                    data-selected={theme === "dark"}
                >
                    <span className="flex items-center">
                        <Icon name="moon" size="m" className="mr-2" />
                        Donker
                    </span>
                    {theme === "dark" && <Icon name="check" size="m" className="" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="data-[selected=true]:bg-selected-default data-[selected=true]:text-content-selected justify-between"
                    onClick={() => setTheme("system")}
                    data-selected={theme === "system"}
                >
                    <span className="flex items-end">
                        <Icon name="ai-assistent" size="m" className="mr-2" />
                        Automatisch
                    </span>
                    {theme === "system" && <Icon name="check" size="m" className="" />}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function Navbar() {
    const { pathname } = useLocation();
    const { user, logout, isAuthenticated, isLoading, previewRole, setPreviewRole, hasRole } = useAuth();
    const [hasPermissions, requestPermission] = useNotificationPermissions();

    const handleLogout = () => {
        try {
            logout();
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!hasRole(UserRole.ADMIN, true)) return;

            if (event.ctrlKey && Number(event.key)) {
                const role = Object.values(UserRole).find((_, index) => index + 1 === Number(event.key));

                if (role) {
                    event.preventDefault();
                    setPreviewRole(previewRole === role ? null : role);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [hasRole, previewRole, setPreviewRole]);

    const getUserInfo = () => {
        if (!user) return { name: "User", initials: "U", profilePicture: "", email: "" };

        const name = user.name ?? user.username ?? "User";
        const initials = name
            .split(" ")
            .map((part: string) => part[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
        const profilePicture = (user.idTokenClaims?.picture as string) ?? "";
        const email = user.username ?? "";

        return { name, initials, profilePicture, email };
    };

    const { name, email } = getUserInfo();

    const enableNotifications = useCallback(() => {
        if (!hasPermissions) {
            requestPermission();
        }
    }, [hasPermissions, requestPermission]);

    // Prepare the user dropdown content
    let userContent = null;

    if (isLoading) {
        userContent = <Icon name="loader" className="text-interactive-default animate-spin" />;
    } else if (isAuthenticated) {
        userContent = (
            <DropdownMenu>
                <DropdownMenuTrigger asChild data-testid="user">
                    <Button icon="user" variant="tertiary" iconOnly />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="text-content-default w-56" align="end" forceMount data-testid="user">
                    <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                            <p className="text-s leading-none font-medium">{name}</p>
                            <p className="text-muted-foreground text-xs leading-none">{email}</p>
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                        <Link to="/profile">
                            <Icon name="user" size="xs" />
                            <span>Profile</span>
                        </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={enableNotifications}>
                        <Icon name="notification" size="xs" />
                        <span>{hasPermissions ? "Notificaties staan aan" : "Zet notificaties aan"}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                        <Icon name="log-out" size="xs" />
                        <span>Log out</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <header className="bg-elevated-1 relative border-b">
            <Container fluid>
                <div className="flex h-(--header-height) items-center justify-between">
                    <div className="flex flex-row items-center justify-between gap-14">
                        <Link to="/" className="flex flex-row items-center justify-between gap-3 text-xl font-bold">
                            <Logo />
                            <h5 className="text-m text-content-emphasis leading-m font-bold">SEIN</h5>
                        </Link>

                        <NavigationMenu>
                            <NavigationMenuList className="gap-m flex h-(--header-height) flex-row">
                                <NavigationMenuItem className="h-full">
                                    <NavigationMenuTrigger
                                        className="relative flex h-full flex-col items-center"
                                        selected={pathname === "/" || pathname.startsWith("/dossier")}
                                    >
                                        <Link to="/" className="m-auto">
                                            Actueel
                                        </Link>
                                    </NavigationMenuTrigger>
                                </NavigationMenuItem>
                                <NavigationMenuItem className="h-full">
                                    <NavigationMenuTrigger
                                        className="relative flex h-full flex-col items-center"
                                        selected={pathname.startsWith("/afgerond")}
                                    >
                                        <Link to="/afgerond" className="m-auto">
                                            Afgerond
                                        </Link>
                                    </NavigationMenuTrigger>
                                </NavigationMenuItem>
                            </NavigationMenuList>
                        </NavigationMenu>
                    </div>

                    <div className="flex items-center gap-4">
                        <CurrentRoleBadge />

                        <PreviewRolesMenu />

                        <ReleaseHighlightMenu />

                        <ThemeToggleDropdown />

                        <RoleGate roles={[UserRole.ADMIN, UserRole.SUPPORTCONSULTANT]}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button icon="settings" variant="tertiary" iconOnly title="Open instellingen" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="text-content-default w-64" align="end">
                                    <DropdownMenuLabel>Admin Panel</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        <RoleGate roles={[UserRole.ADMIN]}>
                                            <DropdownMenuItem asChild>
                                                <Link to="admin/beheer">
                                                    <Icon name="wrench" size="xs" className="mr-2" />
                                                    <span>Admin beheer</span>
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="admin/scenarios">
                                                    <Icon name="prompt" size="xs" className="mr-2" />
                                                    <span>Scenarios</span>
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="admin/vos-oproepen">
                                                    <Icon name="users" size="xs" className="mr-2" />
                                                    <span>VOS oproepen</span>
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="admin/release-highlights">
                                                    <Icon name="news" size="xs" className="mr-2" />
                                                    <span>Release highlights</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        </RoleGate>
                                        <RoleGate roles={[UserRole.SUPPORTCONSULTANT]}>
                                            <DropdownMenuItem asChild>
                                                <Link to="/beheer/vestigingen">
                                                    <Icon name="coffee" size="xs" className="mr-2" />
                                                    <span>Faciliteiten</span>
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/beheer/vossen">
                                                    <Icon name="user" size="xs" className="mr-2" />
                                                    <span>VOS'en</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        </RoleGate>
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </RoleGate>

                        {userContent}
                    </div>
                </div>
            </Container>
        </header>
    );
}
